import { CheckGlobalResourceAccessTask } from './CheckGlobalResourceAccessTask'

/**
 * Пункти меню, які видно не всім.
 *
 * Більшість пунктів бокового меню відкриває реєстр, доступний усій компанії.
 * Винятки (`SPEC-003` §1.3, `SPEC-014` §1.3) — пункти, чий реєстр бачить
 * лише частина ролей: без фільтра решта натрапляла б на 403 рівно там, куди
 * їх привело меню. Етап 0 таких пунктів не має — перелік наповнюють розділи
 * областей.
 *
 * ⚠️ Перелік **явний, а не загальне правило**. Спокусливо було б питати доступ
 * для кожного пункту, чий ключ є в `ResourceCorrespondenceConfig`, — але та
 * мапа задає **заборону за замовчуванням**, і будь-яка затримка чи збій
 * завантаження прав сховали б половину меню. Мовчки: користувач побачив би
 * порожню навігацію й вирішив, що зламався застосунок.
 *
 * ⚠️ Право беремо те саме, що перевіряє бек (ключ Rule у
 * `GET /services/auth/permissions`), а не роль користувача. Ролі на фронті
 * довелося б звіряти з ієрархією `security.yaml`, тобто тримати її другу
 * копію — а успадкування ролей (`ROLE_ADMIN` → `ROLE_ACCOUNTANT` тощо) живе
 * саме там.
 */
const GATED_MENU_ITEMS: Record<string, { resource: string; operation: string }> = {
    // «Довідники» — лише адміністратору (`SPEC-003` §2.3, `SPEC-007` §7.1).
    // Право читання тут не годиться: довідники читає кожен (селекти карток),
    // а створювати статуси бек дозволяє лише адміністратору. Той самий вхід
    // перевіряє й сама сторінка (`CheckDictionaryPageAccessTask`)
    'Setting.Index': { resource: 'Setting.ListClientStatus', operation: 'create' },
    // «Інші перевізники» — лише логістам (з ієрархією — SL, A, SA): бек не дає
    // решті навіть читати список (`SPEC-003` §2.5, `SPEC-014` Г6)
    'Crm.OtherCarrier': { resource: 'Crm.OtherCarrier', operation: 'read_collection' },
    // «Справи» — ролям, що працюють із контрагентами (ACC, K, M, L; з
    // ієрархією — SL, A, SA). «Співробітник» справ не бачить: без фільтра
    // меню привело б його на 403 (`SPEC-011` §10, `SPEC-014` §1.3)
    'Crm.Activity': { resource: 'Crm.Activity', operation: 'read_collection' },
    // Перевезення (`SPEC-025` §3.4.3, `SPEC-020` §7.4): легасі показувало
    // пункти всім, а тут без `read_collection` бек відповість 403 — «Фінанси»
    // запитів і тендерів не бачать (Q2.13); «Рейси» — правами заявки
    'Transportation.Application': { resource: 'Transportation.Application', operation: 'read_collection' },
    'Transportation.Trip': { resource: 'Transportation.Trip', operation: 'read_collection' },
    'Transportation.Request': { resource: 'Transportation.Request', operation: 'read_collection' },
    'Transportation.Tender': { resource: 'Transportation.Tender', operation: 'read_collection' },
    // «Адміністрування диска» — лише адміністраторам (`DISK-006` §2.1): ключ
    // `DiskAdmin` бек віддає з `DiskAdminRule`. Той самий вхід перевіряє й
    // каркас сторінок панелі (`DISK_ADMIN_GATE`)
    'Disk.Admin': { resource: 'Disk.Admin', operation: 'read_collection' },
}

/**
 * Чи показувати пункт меню.
 *
 * Невизначеність трактуємо на користь видимості: пункт, якого немає в переліку,
 * лишається — приховати щось зайве тут коштує дорожче, ніж показати.
 */
export const CheckMenuItemAccessTask = (menuKey?: string | null): boolean => {
    if (!menuKey) return true

    const gate = GATED_MENU_ITEMS[menuKey]

    if (!gate) return true

    return CheckGlobalResourceAccessTask(gate.resource, gate.operation)
}

export { GATED_MENU_ITEMS }
